import React, { useState } from 'react'
import { DataGrid, useGridApiRef } from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import notFound from "../../Contacts/Table/NotFoundData"
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal"
import { LuCopy, LuPencil, LuTrash2 } from "react-icons/lu"

// Local styled component removed in favor of global theme overrides

const CopyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.3333 10.7499V14.2499C13.3333 17.1666 12.1667 18.3332 9.25 18.3332H5.75C2.83333 18.3332 1.66667 17.1666 1.66667 14.2499V10.7499C1.66667 7.83324 2.83333 6.6666 5.75 6.6666H9.25C12.1667 6.6666 13.3333 7.83324 13.3333 10.7499Z" stroke="var(--color-gray-600)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.3333 5.74992V9.24992C18.3333 12.1666 17.1667 13.3333 14.25 13.3333H13.3333V10.7499C13.3333 7.83325 12.1667 6.66659 9.25 6.66659H6.66667V5.74992C6.66667 2.83325 7.83333 1.66659 10.75 1.66659H14.25C17.1667 1.66659 18.3333 2.83325 18.3333 5.74992Z" stroke="var(--color-gray-600)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const TriggersTable = ({ data, onEdit, onCopy, onDelete }) => {
  const apiRef = useGridApiRef()
  const [openDeleteModal, setOpenDeleteModal] = useState(null)
  const [triggerToDelete, setTriggerToDelete] = useState(null)

  const handleDeleteClick = (trigger) => {
    setTriggerToDelete(trigger)
    setOpenDeleteModal(trigger._id)
  }

  const handleConfirmDelete = () => {
    if (triggerToDelete) {
      onDelete(triggerToDelete._id, triggerToDelete.name)
    }
    setOpenDeleteModal(null)
    setTriggerToDelete(null)
  }

  const handleCancelDelete = () => {
    setOpenDeleteModal(null)
    setTriggerToDelete(null)
  }

  const renderActionsCell = (actions) => {
    if (!actions || actions.length === 0) {
      return <span className="text-text-secondary text-sm">No actions</span>
    }

    if (actions.length === 1) {
      return <div className="text-sm">{actions[0].name}</div>
    }

    if (actions.length === 2) {
      return (
        <div className="text-sm">
          {actions.map((action, index) => (
            <div key={action._id || index}>{action.name}</div>
          ))}
        </div>
      )
    }

    // More than 2 actions
    return (
      <div className="text-sm">
        <div>{actions[0].name}</div>
        <div>{actions[1].name}</div>
        <div className="text-text-secondary mt-1">+ {actions.length - 2} more</div>
      </div>
    )
  }

  const columns = [
    {
      field: 'triggerEvent',
      headerName: 'Trigger Event',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="text-sm">
          {params.row.condition?.name || params.row.name}
        </div>
      )
    },
    {
      field: 'actionToRun',
      headerName: 'Action to Run',
      flex: 1,
      sortable: false,
      renderCell: (params) => renderActionsCell(params.row.action)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <div className="flex gap-2 items-center">
          <button
            onClick={() => onCopy(params.row._id)}
            className="p-1 hover:bg-surface rounded transition-colors"
            title="Copy"
          >
            <LuCopy size={18} className="text-text-secondary" />
          </button>
          <button
            onClick={() => onEdit(params.row._id)}
            className="p-1 hover:bg-surface rounded transition-colors"
            title="Edit"
          >
            <LuPencil size={18} className="text-text-secondary" />
          </button>
          <button
            onClick={() => handleDeleteClick(params.row)}
            className="p-1 hover:bg-surface rounded transition-colors"
            title="Delete"
          >
            <LuTrash2 size={18} className="text-text-secondary" />
          </button>
        </div>
      )
    }
  ]

  const rows = data.map((trigger) => ({
    id: trigger._id,
    ...trigger
  }))

  return (
    <>
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        autoHeight
        getRowHeight={() => 'auto'}
        localeText={{
          noRowsLabel: notFound()
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-virtualScroller': {
            minHeight: rows.length === 0 ? '200px' : 'auto'
          },
          '& .MuiDataGrid-cell': {
            py: 1.5,
            alignItems: 'flex-start',
          },
        }}
      />

      <ConfirmModal
        open={!!openDeleteModal}
        setOpen={(value) => !value && handleCancelDelete()}
        onClose={handleCancelDelete}
        functionButton={handleConfirmDelete}
        modalButtonTitle="Delete"
        title="Delete Trigger"
        cancelLeft={true}
      >
        <p>Are you sure you want to delete trigger "{triggerToDelete?.name || triggerToDelete?.condition?.name}"?</p>
        <p className="mt-2">This action cannot be undone.</p>
      </ConfirmModal>
    </>
  )
}

export default TriggersTable
